if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();
        document.body.classList.add('sticky-atc-bar-enabled');
      }

      get variantIdSelect() {
        return this.querySelector('[name="id"]');
      }

      // product-info owns the "shopper hasn't picked a size yet" state; the bar
      // reads it off the attribute so it doesn't matter which element upgrades
      // first.
      get optionsPending() {
        return this.mainProductInfo?.dataset.optionsPending === 'true';
      }

      connectedCallback() {
        this.productFormActions = document.querySelector('.main-product-form');
        this.mainProductInfo = document.querySelector('product-info');
        this.container = this.closest('.sticky-atc-bar');

        this.variantData = this.getVariantData();
        this.colorIndex = parseInt(this.dataset.colorIndex, 10);
        this.isSyncingFromMain = false;
        this.restoreScrollAfterSync = false;
        this.savedScrollY = 0;
        this.colorImageMap = this.buildColorImageMap();

        // [name="id"] es siempre un input hidden: el dropdown custom escribe
        // ahí y despacha `change`, así que un solo listener cubre todo.
        this.variantSelect = this.querySelector('[name="id"]');
        this.currentVariant = this.getSelectedVariant();

        if (this.optionsPending) this.showSizePlaceholder();

        if (this.variantSelect) {
          this.onSelectChange = this.onSelectChange.bind(this);
          this.variantSelect.addEventListener('change', this.onSelectChange);
        }

        this.initSizePicker();

        this.submitButton = this.querySelector('[type="submit"], [name="add"]');
        if (this.submitButton) {
          this.onAddClickGuard = this.onAddClickGuard.bind(this);
          this.submitButton.addEventListener('click', this.onAddClickGuard, true);
        }

        // product-info re-render .focus()es the off-screen main input, scrolling
        // the page; undo that when focus lands in product-info after a bar change.
        this.onDocumentFocusIn = this.onDocumentFocusIn.bind(this);
        document.addEventListener('focusin', this.onDocumentFocusIn, true);

        this.init();
      }

      disconnectedCallback() {
        this.unsubscribeVariant?.();
        this.observer?.disconnect();
        document.removeEventListener('focusin', this.onDocumentFocusIn, true);
        this.submitButton?.removeEventListener('click', this.onAddClickGuard, true);
        this.sizePicker?.removeEventListener('click', this.onPickerClick);
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);
        clearTimeout(this.restoreScrollTimer);
      }

      onDocumentFocusIn(event) {
        if (!this.restoreScrollAfterSync) return;
        if (this.mainProductInfo && this.mainProductInfo.contains(event.target)) {
          window.scrollTo({ top: this.savedScrollY, left: 0, behavior: 'instant' });
          this.restoreScrollAfterSync = false;
        }
      }

      // Sin las variantes ocultas por `custom.hide_variant`
      // (data-hidden-variants, ver snippets/hidden-variant-ids.liquid): la
      // lista de talles que se rearma al cambiar de color sale de acá.
      getVariantData() {
        if (!this.variantData) {
          const script = this.container.querySelector('[data-sticky-variant-json]');
          const hidden = script.dataset.hiddenVariants || '';
          this.variantData = JSON.parse(script.textContent).filter(
            (variant) => !hidden.includes(`,${variant.id},`)
          );
        }
        return this.variantData;
      }

      init() {
        if (!this.productFormActions) {
          this.container.classList.add('sticky-atc-bar--show');
          return;
        }
        this.productId = this.dataset.productId;

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.checkDevice.bind(this);
        this.checkDevice();

        const rootMargin = `${this.productFormActions.offsetHeight}px 0px 0px 0px`;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.container.classList.toggle('sticky-atc-bar--show', !entry.isIntersecting);
            });
          },
          { threshold: 1, rootMargin }
        );
        this.observer.observe(this.productFormActions);
        this.syncWithMainProductForm();
      }

      checkDevice() {
        document.documentElement.style.setProperty('--sticky-atc-bar-height', this.clientHeight + 'px');
      }

      buildColorImageMap() {
        const map = {};
        if (isNaN(this.colorIndex) || this.colorIndex < 0) return map;
        this.variantData.forEach((variant) => {
          const color = variant.options[this.colorIndex];
          if (map[color]) return;
          if (variant.featured_image && variant.featured_image.src) {
            map[color] = variant.featured_image.src;
          }
        });
        return map;
      }

      // -- labels & options --

      buildLabel(variant) {
        if (isNaN(this.colorIndex) || this.colorIndex < 0) return variant.title;
        const nonColor = variant.options.filter((value, i) => i !== this.colorIndex && value);
        return nonColor.length ? nonColor.join(' / ') : variant.title;
      }

      getSelectedVariant() {
        if (!this.variantSelect) return this.variantData[0] || null;
        return this.variantData.find((v) => v.id === Number(this.variantSelect.value)) || null;
      }

      buildOptionsForColor(variant) {
        const panel = this.sizePanel;
        if (!panel || !variant) return;
        const hasColor = !isNaN(this.colorIndex) && this.colorIndex >= 0;
        const color = hasColor ? variant.options[this.colorIndex] : null;
        const matches = this.variantData.filter((v) => !hasColor || v.options[this.colorIndex] === color);
        const heading = panel.querySelector('.sticky-size-picker__heading');

        panel.innerHTML = matches
          .map(
            (v) =>
              `<button type="button" role="option" class="sticky-size-picker__option" data-variant-id="${v.id}"` +
              `${v.available ? '' : ' disabled'} aria-selected="${v.id === variant.id ? 'true' : 'false'}">` +
              `${this.buildLabel(v)}</button>`
          )
          .join('');
        if (heading) panel.prepend(heading);

        if (this.variantSelect) this.variantSelect.value = String(variant.id);
        this.syncPickerLabel();

        // El usuario cambió de color sin haber elegido talle.
        if (this.optionsPending) this.showSizePlaceholder();
      }

      // Hasta que se elige talle el trigger dice "Elegí tu talle" y el form no
      // lleva variant id. El botón queda habilitado a propósito: el gris se leía
      // como "sin stock"; lo que frena el submit es onAddClickGuard.
      showSizePlaceholder() {
        if (this.variantSelect) this.variantSelect.value = '';
        this.syncPickerLabel();
      }

      // -- dropdown custom de talle --

      get sizePicker() {
        return this.querySelector('[data-sticky-size-picker]');
      }
      get sizePanel() {
        return this.querySelector('[data-sticky-size-panel]');
      }
      get sizeTrigger() {
        return this.querySelector('[data-sticky-size-trigger]');
      }

      initSizePicker() {
        if (!this.sizePicker) return;

        this.onPickerClick = this.onPickerClick.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onDocumentKeydown = this.onDocumentKeydown.bind(this);

        this.sizePicker.addEventListener('click', this.onPickerClick);
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeydown);

        this.syncPickerLabel();
      }

      onPickerClick(event) {
        const trigger = event.target.closest('[data-sticky-size-trigger]');
        if (trigger) {
          this.toggleSizePanel(trigger.getAttribute('aria-expanded') !== 'true');
          return;
        }
        const option = event.target.closest('.sticky-size-picker__option');
        if (!option || option.disabled) return;

        const input = this.variantSelect;
        if (input) {
          input.value = option.dataset.variantId;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.toggleSizePanel(false);
      }

      toggleSizePanel(open) {
        const panel = this.sizePanel;
        const trigger = this.sizeTrigger;
        if (!panel || !trigger) return;
        panel.hidden = !open;
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      }

      onDocumentClick(event) {
        if (this.sizePicker && this.sizePicker.contains(event.target)) return;
        this.toggleSizePanel(false);
      }

      onDocumentKeydown(event) {
        if (event.key !== 'Escape') return;
        this.toggleSizePanel(false);
      }

      // Espeja el hidden input en el label del trigger y en los aria-selected.
      syncPickerLabel() {
        const trigger = this.sizeTrigger;
        if (!trigger) return;

        const label = trigger.querySelector('[data-sticky-size-label]');
        const variant = this.getSelectedVariant();

        if (label) {
          // Sin talle elegido el boceto muestra el nombre de la opción ("Size"),
          // que el Liquid deja en data-placeholder del contenedor.
          const placeholder = this.querySelector('[data-sticky-size-picker]')?.dataset.placeholder;
          label.textContent = variant ? this.buildLabel(variant) : placeholder || FoxTheme.variantStrings.chooseSize;
        }
        trigger.classList.toggle('is-filled', !!variant);
        if (variant) trigger.classList.remove('has-error');

        this.querySelectorAll('.sticky-size-picker__option').forEach((option) => {
          const on = !!variant && Number(option.dataset.variantId) === variant.id;
          option.setAttribute('aria-selected', on ? 'true' : 'false');
        });
      }

      // Igual que en la PDP: se cancela el click del botón, no el submit del
      // form. stopImmediatePropagation y no stopPropagation, porque puede haber
      // otro listener sobre el mismo botón y stopPropagation solo frena ancestros.
      onAddClickGuard(event) {
        if (!this.optionsPending) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        this.toggleSizePanel(true);
        this.sizeTrigger?.classList.add('has-error');

        document.dispatchEvent(
          new CustomEvent('size:required', { detail: { productId: this.dataset.productId } })
        );
      }

      // -- change handling --

      onSelectChange() {
        if (this.isSyncingFromMain) return;
        if (this.variantSelect && this.variantSelect.value === '') return;

        const variant = this.getSelectedVariant();
        this.currentVariant = variant;

        this.savedScrollY = window.scrollY;
        this.restoreScrollAfterSync = true;
        clearTimeout(this.restoreScrollTimer);
        this.restoreScrollTimer = setTimeout(() => {
          this.restoreScrollAfterSync = false;
        }, 2000);

        // Propagate before applying: picking a size here clears product-info's
        // pending state synchronously, so applyVariantToBar won't snap the select
        // back to the placeholder for the length of the re-render.
        this.propagateToMain(variant);
        this.applyVariantToBar(variant);
      }

      applyVariantToBar(variant) {
        if (variant) {
          this.variantIdSelect.value = variant.id;
          this.syncPickerLabel();
          this.updatePrice();
          this.updateImage();
          // Mientras falta el talle, `variant` es la que resolvió el color y
          // puede ser un talle agotado dentro de un color con stock: deshabilitar
          // por eso se lee como "sin stock". Se mira si queda alguna fila elegible.
          const soldOut = this.optionsPending ? !this.hasPickableSize() : !variant.available;
          this.updateButton(soldOut, FoxTheme.variantStrings.soldOut, false);
        } else {
          this.variantIdSelect.value = '';
          // Sin variante y con talle pendiente es el estado normal de entrada,
          // no una combinación no disponible: no se toca el botón.
          if (!this.optionsPending) {
            this.updateButton(true, FoxTheme.variantStrings.unavailable, true);
          }
        }

        // The price and image above track the colour's first variant, but the
        // shopper still owes us a size.
        if (this.optionsPending) this.showSizePlaceholder();
      }

      // ¿Queda algún talle por elegir? Si no hay panel (producto de una sola
      // variante) cae a la disponibilidad de la variante resuelta.
      hasPickableSize() {
        const options = this.querySelectorAll('.sticky-size-picker__option');
        if (!options.length) return !!this.getSelectedVariant()?.available;
        return Array.from(options).some((o) => !o.disabled);
      }

      // -- sync sticky -> main --

      propagateToMain(variant) {
        if (!variant) return;
        const productInfo = document.querySelector('product-info');
        const mainVariantSelects = productInfo && productInfo.querySelector('variant-selects');
        if (!mainVariantSelects) return;

        const groups = Array.from(mainVariantSelects.children).filter((el) =>
          el.classList.contains('product-form__input')
        );

        variant.options.forEach((value, i) => {
          const group = groups[i];
          if (!group) return;

          const radio = group.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
          if (radio) {
            if (!radio.checked) {
              radio.checked = true;
              radio.dispatchEvent(new Event('change', { bubbles: true }));
            }
            return;
          }

          const mainSelect = group.querySelector('select');
          if (mainSelect && mainSelect.value !== value) {
            mainSelect.value = value;
            mainSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      // -- sync main -> sticky --

      syncWithMainProductForm() {
        this.unsubscribeVariant = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          (event) => {
            const mainSectionId =
              this.mainProductInfo?.dataset.originalSection || this.mainProductInfo?.dataset.section;
            if (!mainSectionId || event.data.sectionId !== mainSectionId) return;

            const variant = event.data.variant;
            if (!variant) return;

            this.isSyncingFromMain = true;
            try {
              this.buildOptionsForColor(variant);
              this.currentVariant = variant;
              this.applyVariantToBar(variant);
            } finally {
              this.isSyncingFromMain = false;
            }
          }
        );
      }

      // -- UI updates --

      updateButton(disable = true, text, modifyClass = true) {
        const productForm = this.querySelector('.sticky-atc-bar__form');
        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('span');
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = FoxTheme.variantStrings.addToCart;
        }
      }

      updateImage() {
        const img = this.querySelector('.sticky-atc-bar__product-image img');
        if (!img || !this.currentVariant) return;

        let src =
          this.currentVariant.featured_image && this.currentVariant.featured_image.src
            ? this.currentVariant.featured_image.src
            : this.colorImageMap[this.currentVariant.options[this.colorIndex]];
        if (!src) return;

        const sized = (w) => src.replace(/\.(jpg|jpeg|png|gif|webp)/i, `_${w}x.$1`);
        img.srcset = `${sized(70)} 70w, ${sized(140)} 140w`;
        img.src = sized(140);
      }

      updatePrice() {
        if (!this.currentVariant) return;
        const classes = { onSale: 'f-price--on-sale', soldOut: 'f-price--sold-out' };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );
        const unitPrice = unitPriceWrapper.querySelector('.f-price__unit');
        const { compare_at_price, price, unit_price_measurement } = this.currentVariant;

        if (compare_at_price && compare_at_price > price) priceWrapper.classList.add(classes.onSale);
        else priceWrapper.classList.remove(classes.onSale);

        if (!this.currentVariant.available) priceWrapper.classList.add(classes.soldOut);
        else priceWrapper.classList.remove(classes.soldOut);

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = `<s>${FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat)}</s>`)
          );
        } else {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          const baseUnit =
            FoxTheme.Currency && typeof FoxTheme.Currency.getBaseUnit === 'function'
              ? FoxTheme.Currency.getBaseUnit(this.currentVariant)
              : '';
          unitPrice.innerHTML = `<span>${FoxTheme.Currency.formatMoney(
            this.currentVariant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${baseUnit}</span>`;
        } else {
          unitPriceWrapper.classList.add('hidden');
        }
      }
    }
  );
}
